import { Cloud } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { cloudSyncStatusBadgeVariant } from '@/lib/hyperBackup'
import {
  normaliseToolkitStatus,
  toolkitStatusLabel,
} from '@/lib/toolkitHealth'
import { cn } from '@/lib/utils'

function PostureBadge({ status }) {
  const normalised = normaliseToolkitStatus(status)
  if (!normalised) return null
  const variant =
    normalised === 'PASS'
      ? 'success'
      : normalised === 'WARN'
        ? 'warning'
        : normalised === 'FAIL'
          ? 'destructive'
          : 'secondary'
  return <Badge variant={variant}>{toolkitStatusLabel(status)}</Badge>
}

/**
 * Prominent Cloud Sync block for NAS / Backup host cards.
 */
export function CloudSyncConnectionsPanel({
  connections,
  postureStatus,
  count,
  className,
}) {
  const rows = connections ?? []
  const total = count ?? rows.length
  const overall = normaliseToolkitStatus(postureStatus)
  const panelTone =
    overall === 'FAIL'
      ? 'border-destructive/40 bg-destructive/5'
      : overall === 'WARN'
        ? 'border-amber-500/40 bg-amber-500/5'
        : 'border-sky-500/35 bg-sky-500/5'

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border-2 px-3 py-3 shadow-sm',
        panelTone,
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-sky-600 text-white">
          <Cloud className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold tracking-tight text-foreground">
            Cloud Sync
          </p>
          <p className="text-xs text-muted-foreground">
            {total} connection{total === 1 ? '' : 's'} to cloud storage
          </p>
        </div>
        <PostureBadge status={postureStatus} />
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No Cloud Sync connections found in the latest snapshot.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((connection, index) => (
            <li
              key={`${connection.task_name || 'unknown'}-${connection.connection_id || index}`}
              className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-sky-500/20 bg-background/80 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="text-sm font-semibold text-foreground">
                  {connection.task_name || 'Unknown connection'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {connection.cloud_type || 'Cloud storage'}
                </p>
                {(connection.sync_folder || connection.remote_path) && (
                  <p className="text-xs text-muted-foreground break-all">
                    {[connection.sync_folder, connection.remote_path]
                      .filter(Boolean)
                      .join(' → ')}
                  </p>
                )}
              </div>
              <div className="shrink-0">
                {connection.status_label ? (
                  <Badge
                    variant={cloudSyncStatusBadgeVariant(connection.status)}
                    className="px-2.5 py-1 text-xs font-semibold"
                  >
                    {connection.status_label}
                  </Badge>
                ) : (
                  <span className="text-sm text-muted-foreground">—</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
